import type * as React from "react";
import {
  Image as RNImage,
  type AccessibilityRole,
  type ImageSourcePropType,
  type ImageStyle,
  type StyleProp,
} from "react-native";

export type ImageSource =
  | { uri?: string | undefined }
  | { uri?: string | undefined }[]
  | string
  | number;

export interface ImageProps {
  /**
   * The image to display. Accepts a URI string, a `{ uri }` object, an array of
   * URI objects, or a local asset reference (the result of `require('./image.png')`).
   */
  source: ImageSource;
  /**
   * Additional styles applied to the image element.
   */
  style?: StyleProp<ImageStyle>;
  /**
   * A human-readable label for screen readers. Required when `accessible` is not `false`.
   */
  accessibilityLabel?: string;
  /**
   * Whether the image is exposed to the accessibility tree. Defaults to `true`.
   * Set to `false` for purely decorative images.
   */
  accessible?: boolean;
  /**
   * When `true`, hides this element and its children from the accessibility tree (iOS).
   */
  accessibilityElementsHidden?: boolean;
  /**
   * Controls whether the view is important for accessibility on Android.
   * Use `'no-hide-descendants'` to hide the subtree.
   */
  importantForAccessibility?: "auto" | "yes" | "no" | "no-hide-descendants";
  /**
   * Accessibility role used by assistive technologies to describe the element type.
   */
  accessibilityRole?: AccessibilityRole;
  /**
   * When `true`, the image ignores the system colour inversion setting (iOS).
   * Useful for photographs and images where colour accuracy matters.
   */
  accessibilityIgnoresInvertColors?: boolean;
  /**
   * Controls how the image is scaled to fit its container.
   */
  resizeMode?: "cover" | "contain" | "stretch" | "center";
  /**
   * Intrinsic width of the image in pixels. Useful for runtime-computed
   * dimensions that cannot be expressed as a static css.create value.
   */
  width?: number;
  /**
   * Intrinsic height of the image in pixels. Useful for runtime-computed
   * dimensions that cannot be expressed as a static css.create value.
   */
  height?: number;
  /**
   * Test identifier forwarded to the native element for use in automated tests.
   */
  testID?: string;
}

function resolveRNSource(source: ImageSource): ImageSourcePropType {
  if (typeof source === "string") return { uri: source };
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return source as ImageSourcePropType;
}

function ImageBase({
  source,
  style,
  accessibilityLabel,
  accessible,
  accessibilityElementsHidden,
  importantForAccessibility,
  accessibilityRole,
  accessibilityIgnoresInvertColors,
  resizeMode,
  width,
  height,
  testID,
}: ImageProps): React.JSX.Element {
  return (
    <RNImage
      source={resolveRNSource(source)}
      style={width != null || height != null ? [{ width, height }, style] : style}
      accessibilityLabel={accessibilityLabel}
      accessible={accessible}
      accessibilityElementsHidden={accessibilityElementsHidden}
      importantForAccessibility={importantForAccessibility}
      accessibilityRole={accessibilityRole}
      accessibilityIgnoresInvertColors={accessibilityIgnoresInvertColors}
      resizeMode={resizeMode}
      testID={testID}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
export const Image = Object.assign(ImageBase, {
  resolveAssetSource: RNImage.resolveAssetSource,
}) as typeof ImageBase & {
  getSize: typeof RNImage.getSize;
  resolveAssetSource: typeof RNImage.resolveAssetSource;
};

Object.defineProperty(Image, "getSize", {
  get() {
    return RNImage.getSize;
  },
  configurable: true,
  enumerable: true,
});
